import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const emptyForm = { name: '', options: '', description: '' };

export default function AdminVariants() {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchAttributes = () => {
    setLoading(true);
    api.get('/variants')
      .then((res) => setAttributes(res.data))
      .catch(() => toast.error('Could not load variant attributes'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAttributes, []);

  const openCreateModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (attr) => {
    setForm({
      name: attr.name,
      options: attr.options.join(', '),
      description: attr.description || '',
    });
    setEditingId(attr._id);
    setShowModal(true);
  };

  const removeAttribute = async (id) => {
    if (!window.confirm('Delete this variant attribute permanently?')) return;
    try {
      await api.delete(`/variants/${id}`);
      toast.success('Variant attribute removed');
      fetchAttributes();
    } catch (e) {
      toast.error('Could not delete attribute');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.options.trim()) {
      toast.error('Please specify both attribute name and options');
      return;
    }
    setSaving(true);
    const optionsArray = form.options.split(',').map((o) => o.trim()).filter(Boolean);
    const payload = {
      name: form.name.trim(),
      options: optionsArray,
      description: form.description,
    };

    try {
      if (editingId) {
        await api.put(`/variants/${editingId}`, payload);
        toast.success('Variant attribute updated');
      } else {
        await api.post('/variants', payload);
        toast.success('New variant attribute created');
      }
      setShowModal(false);
      setForm(emptyForm);
      fetchAttributes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save variant attribute');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 text-[#2B1810]">
      {/* Header Banner */}
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Product Variants & Attributes</h1>
          <p className="text-xs text-[#8A796C]">Configure global product attributes (e.g. Size, Wood Type) used across product uploads.</p>
        </div>

        <button
          onClick={openCreateModal}
          className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded shadow transition-colors"
        >
          + Add Variant Attribute
        </button>
      </div>

      {/* Grid of Global Variant Attributes */}
      {loading ? (
        <div className="bg-white p-8 rounded-xl border border-[#E8DFD8] text-center text-xs text-[#8A796C]">
          Loading global variant attributes...
        </div>
      ) : attributes.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-[#E8DFD8] text-center space-y-3">
          <p className="text-xs text-[#8A796C]">No global variant attributes configured yet.</p>
          <button onClick={openCreateModal} className="bg-[#C49A45] text-white text-xs font-bold px-4 py-2 rounded">
            Create First Attribute
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {attributes.map((attr) => (
            <div key={attr._id} className="bg-white border border-[#E8DFD8] rounded-xl p-5 shadow-sm space-y-3 flex flex-col justify-between hover:border-[#C49A45] transition-all">
              <div className="space-y-2">
                <div className="flex items-center justify-between border-b border-[#E8DFD8] pb-2">
                  <h3 className="font-heading font-bold text-sm text-[#2B1810]">{attr.name}</h3>
                  <span className="text-[10px] font-bold bg-[#F5EFEB] text-[#C49A45] px-2 py-0.5 rounded border border-[#E8DFD8]">
                    {attr.options.length} Options
                  </span>
                </div>

                {attr.description && (
                  <p className="text-[11px] text-[#8A796C]">{attr.description}</p>
                )}

                <div className="pt-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#A8988B] block mb-1.5">Defined Values / Options:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {attr.options.map((opt, i) => (
                      <span key={i} className="text-xs font-bold bg-[#FAF8F5] text-[#2B1810] border border-[#E8DFD8] px-2.5 py-1 rounded-md">
                        {opt}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E8DFD8]">
                <button
                  onClick={() => openEditModal(attr)}
                  className="bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#E8DFD8] text-[#2B1810] font-bold text-[11px] py-1 px-3 rounded transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => removeAttribute(attr._id)}
                  className="bg-red-50 hover:bg-red-100 text-red-700 font-bold text-[11px] py-1 px-3 rounded transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Attribute Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-2xl max-w-lg w-full space-y-4 animate-scaleUp text-[#2B1810]">
            <div className="flex items-center justify-between border-b border-[#E8DFD8] pb-3">
              <h3 className="font-heading font-bold text-lg text-[#2B1810]">
                {editingId ? 'Edit Variant Attribute' : 'Add Global Variant Attribute'}
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-[#8A796C] font-bold hover:text-black">✕</button>
            </div>

            <div>
              <label className="label">Attribute Name (e.g. Size, Wood Type, Fragrance)</label>
              <input
                required
                className="input-field"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Size"
              />
            </div>

            <div>
              <label className="label">Options / Values (Comma Separated)</label>
              <input
                required
                className="input-field"
                value={form.options}
                onChange={(e) => setForm({ ...form, options: e.target.value })}
                placeholder="e.g. S, M, L, XL, 108 Beads, 54 Beads"
              />
              <p className="text-[10px] text-[#8A796C] mt-1">Separate options with commas (e.g., S, M, L or Red Sandalwood, Mysore Sandalwood)</p>
            </div>

            <div>
              <label className="label">Description (Optional)</label>
              <input
                className="input-field"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief internal note"
              />
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-[#E8DFD8]">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="bg-[#FAF8F5] border border-[#E8DFD8] text-[#2B1810] font-bold text-xs uppercase px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                disabled={saving}
                type="submit"
                className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase px-6 py-2 rounded shadow"
              >
                {saving ? 'Saving...' : editingId ? 'Update Attribute' : 'Save Attribute'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
